<template>
  <span class="status-item ds-width-full">
    <span
      :role="isAriaHeading ? 'heading' : null"
      :aria-level="isAriaHeading ? '3' : null"
      :aria-describedby="description ? itemDescription : null"
      class="status-item__header ds-display-flex ds-width-full"
      :class="isLeftAligned ? 'ds-md-justify-content-flex-start' : 'ds-md-justify-content-space-between'"
      data-j-header
    >
      <span v-if="loading" class="status-loading" :style="{ height: '20px', width: isScreenSmall ? '249px' : '300px' }" />
      <span v-else class="ds-display-flex ds-align-items-flex-start">
        <DsIconFrame
          aria-hidden="true"
          size="24"
          align-y="center"
          align-x="center"
          class="status-item__header__icon"
        >
          <DsIcon
            :class="`ds-color-${icon.color}`"
            :name="icon.name"
            :category="icon.category"
          />
        </DsIconFrame>
        <span
          class="ds-margin-left-12 ds-text ds-font-weight-bold ds-color-black"
          data-j-title
        >
          {{ title }}
        </span>
      </span>
      <span
        v-if="loading"
        class="status-loading"
        :class="[
          { 'ds-margin-left-12': isLeftAligned && !isScreenSmall },
          { 'ds-margin-top-12': isScreenSmall }
        ]"
        :style="{ height: '20px', width: '150px' }"
      />
      <span
        v-else
        class="ds-text-small ds-color-grey-450"
        :class="[
          { 'status-item--icon-spacing': isScreenSmall },
          { 'ds-margin-left-12': isLeftAligned },
          computedSpacing
        ]"
        data-j-date
      >
        {{ date }}
      </span>
    </span>
    <template v-if="description">
      <span v-if="loading" class="ds-display-flex ds-flex-direction-column ds-margin-left-24">
      <span
        v-for="index in 4"
        :key="`status-item-description-loading-${index}`"
        class="ds-margin-top-6 status-loading status-item__description-box__description--loading"
        :class="[
          `status-item__description-box__description--loading--${index}`,
          { 'ds-margin-top-12': index === 1 }
        ]"
        :style="{ height: '20px' }"
      />
    </span>
    <span v-else class="status-item__description-box">
      <span
        v-if="withVerticalLine"
        aria-hidden="true"
        class="status-item__description-box__vertical-line"
      />
      <span
        :id="itemDescription"
        class="ds-rich-text status-item__description-box__description status-item--icon-spacing"
        data-j-description
        v-html="content"
      />
    </span>
    </template>
  </span>
</template>

<script>
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import DsIcon from 'website-design-system/src/atoms/icon/icon';
import DsIconFrame from 'website-design-system/src/atoms/icon/icon-frame';
import uid from 'website-design-system/src/utils/mixins/uid/uid';
import { isScreenSmall } from '@/utils/screen.js';

export default {
  name: 'StatusItem',
  components: { DsIcon, DsIconFrame },
  mixins: [uid],
  inject: ['screen', 'loading'],
  props: {
    icon: {
      type: Object,
      default: () => ({ name: '', category: '', color: '' }),
      validator: obj => ['name', 'category', 'color'].every(key => key in obj)
    },
    title: {
      type: String,
      default: ''
    },
    date: {
      type: String,
      default: ''
    },
    isAriaHeading: {
      type: Boolean,
      default: true
    },
    alignX: {
      type: String,
      default: 'right',
      validator: str => ['left', 'right'].includes(str)
    },
    withSmallSpacing: {
      type: Boolean,
      default: false
    },
    withVerticalLine: {
      type: Boolean,
      default: false
    },
    description: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    itemDescription: ''
  }),
  computed: {
    isScreenSmall () {
      return isScreenSmall(this.screen.name)
    },
    isLeftAligned () {
      return this.alignX === 'left'
    },
    computedSpacing () {
      if (this.isScreenSmall) {
        return this.withSmallSpacing ? 'ds-margin-top-6' : 'ds-margin-top-12'
      } else {
        return null
      }
    },
    content () {
      return documentToHtmlString(this.description?.json)
    }
  },
  created() {
    this.itemDescription = `status-item-description-${this.uid}`
  }
};
</script>

<style lang="scss" scoped>
@import 'website-design-system/src/foundation/mixins/mixins';
@import 'website-design-system/src/foundation/tokens/variables';

.status-item {
  &__header {
    align-items: flex-start;
    flex-direction: column;

    @include screen ('medium') {
      align-items: center;
      flex-direction: row;
      flex-grow: 1;
    }

    &__icon {
      :deep(.icon) {
        transform: none;
      }
    }
  }

  &--icon-spacing {
    margin-left: calc($spacing-24 + $spacing-12);
  }

  &__description-box {
    display: block;
    position: relative;

    &__vertical-line {
      border-left: $border-width-1 $border-style $color-grey-100;
      display: inline-block;
      height: calc(100% + $spacing-24);
      margin: -16px 10px 0;
      position: absolute;

      @include screen ('medium') {
        height: calc(100% + $spacing-6);
        margin-top: $spacing-6;
      }
    }

    &__description {
      display: block;
      margin-top: $spacing-12;

      &--loading {
        width: 173px;

        @include screen ('medium') {
          width: 367px;
        }

        &--2 {
          width: 205px;

          @include screen ('medium') {
            width: 433px;
          }
        }

        &--3 {
          width: 231px;

          @include screen ('medium') {
            width: 489px;
          }
        }
      }
    }
  }
}
</style>
